/**
 * "Has the candidate answered THIS question?" — the predicate behind the
 * Required gate in `routes/Question.tsx`.
 *
 * Extracted because it was a seven-branch ternary whose LAST branch was the
 * fallback for "code", so a question type nobody added to the chain silently
 * read as *unanswered*. That is what happened to `likert` (EF-9/FEAT-11): the
 * save path knew a statement is mcq-shaped on the wire and stored it in the
 * mcq selection, but this chain did not, so every statement answered False,
 * every statement is Required, and the Required gate swallowed the advance —
 * a candidate could pick an option and press "Save & next" forever without
 * moving or even saving. A pure function with a test per type is what keeps
 * the next new type from landing in the same hole.
 *
 * The rule: a type is answered when ITS OWN input carries content. Never
 * "some field somewhere is non-empty" — a code draft left on question 3 must
 * not make statement 1 look answered.
 */

/** The question types the candidate app can render. */
export type CandidateQuestionType =
  | "mcq"
  | "true_false"
  | "likert"
  | "mcq_multi"
  | "open_ended"
  | "file_upload"
  | "code";

export interface AnswerState {
  /** Single-select value — mcq, true_false AND likert ("1"–"5") all use it. */
  mcqSelection: string | null;
  mcqMultiSelection: string[];
  freeText: string;
  /** EF-11: the candidate's uploaded file, or null when nothing is stored. */
  savedFile: unknown | null;
  code: string;
  /** B9: the question's starter — the editor opens ON it, so leaving it as
   *  it was is not an answer. Omitted / null = no starter. */
  starterCode?: string | null;
}

const normalizedCode = (code: string | null | undefined): string =>
  (code ?? "").replace(/\r\n/g, "\n").trim();

/** B9 (2026-09-27 pre-ship audit): code the candidate actually wrote — not
 *  blank, and not the starter left untouched (trimmed, CRLF folded). The
 *  server's `_code_is_answered` (backend/api/candidate.py) is the same rule,
 *  so this gate and `/submit`'s Required backstop agree. */
function codeIsAnswered(state: AnswerState): boolean {
  const code = normalizedCode(state.code);
  return code.length > 0 && code !== normalizedCode(state.starterCode);
}

export function hasAnswerFor(
  type: CandidateQuestionType | string,
  state: AnswerState,
): boolean {
  switch (type) {
    // EF-9: a statement is single-select and shares the mcq selection — the
    // same shape `saveCurrent` puts on the wire as `selected_answer`.
    case "mcq":
    case "true_false":
    case "likert":
      return Boolean(state.mcqSelection);
    case "mcq_multi":
      return state.mcqMultiSelection.length > 0;
    case "open_ended":
      return state.freeText.trim().length > 0;
    case "file_upload":
      return state.savedFile !== null;
    case "code":
      return codeIsAnswered(state);
    default:
      // An UNKNOWN type must not be treated as answered — that would let a
      // candidate skip a Required question. Code is the historical fallback
      // and keeps that behaviour for anything code-shaped added later.
      return codeIsAnswered(state);
  }
}
